import { useEffect, useState } from 'react';
import { track, trackOnce } from '../lib/track.js';
import { Link, useParams } from 'react-router-dom';
import { PLAYBOOK_POSTS, playbookBySlug } from '../data/playbook.js';
import { docBai, docBaiDaXuatBan } from '../lib/playbook.js';
import { baiLienQuan, categorySlug, ngayVN, ORG_AUTHOR, STORY_TYPES } from '../lib/playbookModel.js';
import { capNhatHead } from '../lib/headClient.js';
import { PlaybookFigure, PlaybookQuote, PlaybookTable, StoryBadge } from '../components/PlaybookParts.jsx';
// Chữ trong bài hỗ trợ [[slug|chữ]] (từ điển) và [[/duong-dan|chữ]] (trang trong site), như trang từ điển.
import DoanVan from '../components/DoanVan.jsx';
import TemplateDownload from '../components/TemplateDownload.jsx';
import NextStep from '../components/NextStep.jsx';
import NhanTram, { TramTiepTheo } from '../components/NhanTram.jsx';
import { tramCuaBai } from '../data/lo-trinh.js';
import { PLAYBOOK, PLAYBOOK_THEO_CHU_DE, PLAYBOOK_MAC_DINH } from '../data/nextSteps.js';
import XemNhanh from "../components/VideoNgan.jsx";
import { videoCuaTrang } from "../data/videos.js";

/** Bước tiếp theo của một bài: cấu hình riêng → công cụ trong bài → theo chủ đề. */
function buocCuaBai(post) {
  if (PLAYBOOK[post.slug]) return PLAYBOOK[post.slug];
  if (post.relatedTools?.length) {
    return post.relatedTools.map((t) => ({ to: `/cong-cu/${t.slug}`, label: t.label || 'Mở công cụ' }));
  }
  return PLAYBOOK_THEO_CHU_DE[post.category] || PLAYBOOK_MAC_DINH;
}

const HOST = 'https://fundraising.vn';
const HOP_THU = 'xin-chao@fundraising.vn';

/**
 * Khối kết nối cuối bài Spotlight. Mọi yêu cầu đi qua hộp thư của site để đo
 * được và để founder duyệt trước khi lộ liên hệ hay deck; trang không hiện
 * email, số điện thoại của founder.
 */
function SpotlightKetNoi({ post }) {
  const tieuDe = `Yêu cầu kết nối: ${post.author || post.title}`;
  const noiDung = [
    `Startup: ${post.author || post.title}`,
    `Bài: ${HOST}/playbook/${post.slug}`,
    '',
    'Tên và vai trò của bạn:',
    'Quỹ hoặc tổ chức:',
    'Vì sao bạn quan tâm (ngành, giai đoạn, quy mô séc):',
  ].join('\n');
  const href = `mailto:${HOP_THU}?subject=${encodeURIComponent(tieuDe)}&body=${encodeURIComponent(noiDung)}`;
  return <aside className="pb-spotlight-cta" aria-labelledby="spotlight-ket-noi">
    <h2 id="spotlight-ket-noi">Bạn là nhà đầu tư quan tâm?</h2>
    <p>Gửi yêu cầu kết nối, fundraising.vn chuyển tới founder trong 1 đến 2 ngày làm việc. Thông tin vòng gọi vốn và pitch deck được gửi riêng khi founder đồng ý.</p>
    <a className="nut" href={href} onClick={() => track('spotlight_connect_click', { article: post.slug })}>Gửi yêu cầu kết nối</a>
    <p className="nho">Founder muốn startup của mình xuất hiện ở đây? Bắt đầu bằng <Link to="/nhan-xet-deck">gửi deck để được nhận xét</Link>.</p>
  </aside>;
}

function Section({ section, index }) {
  const img = section.image && <PlaybookFigure image={section.image} />;
  return <section id={`phan-${index + 1}`}>
    {section.heading && <h2>{section.heading}</h2>}
    {section.image?.position === 'dau' && img}
    {section.paragraphs?.map((paragraph, i) => <p key={i}><DoanVan chu={paragraph} /></p>)}
    <PlaybookQuote quote={section.quote} />
    {section.image?.position !== 'dau' && img}
    <PlaybookTable table={section.table} nhan={section.heading} />
    {section.notes?.map((note, i) => <p key={`n${i}`}><DoanVan chu={note} /></p>)}
    {section.bullets?.length > 0 && <ul>{section.bullets.map((bullet, i) => <li key={i}><DoanVan chu={bullet} /></li>)}</ul>}
  </section>;
}

export default function PlaybookArticle() {
  const { slug } = useParams();
  const [post, setPost] = useState(() => playbookBySlug(slug));
  const [loading, setLoading] = useState(!post);
  const [related, setRelated] = useState(() => (post ? baiLienQuan(post, PLAYBOOK_POSTS) : []));

  useEffect(() => {
    let live = true;
    docBai(slug).then((value) => { if (live) { setPost(value); setLoading(false); } });
    return () => { live = false; };
  }, [slug]);

  // Bài đã gắn trạm thì cuối bài là "Trạm tiếp theo", không cần danh sách
  // bài liên quan. Bài chưa gắn trạm vẫn giữ "Đọc tiếp" để không cụt đường.
  const tram = tramCuaBai(post?.slug);

  useEffect(() => {
    if (!post || tram) return;
    let live = true;
    docBaiDaXuatBan().then((all) => { if (live) setRelated(baiLienQuan(post, all)); });
    return () => { live = false; };
  }, [post, tram]);

  useEffect(() => {
    if (loading) return;
    if (!post || post.status === 'DRAFT') { capNhatHead({ title: 'Chưa tìm thấy bài | Fundraising Playbook', noindex: true }); return; }
    capNhatHead({
      title: `${post.seoTitle || post.title} | Fundraising Playbook`,
      description: post.seoDescription || post.excerpt,
      canonical: `${HOST}/playbook/${post.slug}`,
      image: post.ogImage || post.coverImage,
    });
  }, [post, loading]);

  // Đọc hết: cuộn qua 75% chiều dài bài. Mỗi bài ghi một lần mỗi phiên.
  useEffect(() => {
    if (loading || !post || post.status === 'DRAFT') return undefined;
    const onScroll = () => {
      const el = document.querySelector('.playbook-article');
      if (!el) return;
      const r = el.getBoundingClientRect();
      const daDoc = (window.innerHeight - r.top) / Math.max(1, r.height);
      if (daDoc >= 0.75) {
        trackOnce('doc:' + post.slug, 'playbook_read', { article: post.slug });
        window.removeEventListener('scroll', onScroll);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [post, loading]);

  if (loading) return <div className="surface section-space"><p>Đang mở Playbook…</p></div>;
  if (!post || post.status === 'DRAFT') return <div className="surface section-space empty-state"><h1>Chưa tìm thấy bài này</h1><Link className="nut" to="/playbook">Về Playbook</Link></div>;

  const note = STORY_TYPES[post.storyType]?.note;
  const toc = post.sections.map((s, i) => ({ s, i })).filter(({ s }) => s.heading);
  return <article className="van-ban playbook-article">
    <nav className="ngay" aria-label="Breadcrumb"><Link to="/playbook">Playbook</Link> / <Link to={`/playbook/chu-de/${categorySlug(post.category)}`}>{post.category}</Link></nav>
    <NhanTram tram={tram} trang="playbook" />
    <p className="pb-type-line"><StoryBadge type={post.storyType} /></p>
    <h1>{post.title}</h1>
    <p className="tom-tat">{post.excerpt}</p>
    <div className="article-byline">
      <span className="pb-author"><strong>{post.author}</strong>{post.authorRole && <>, {post.authorRole}</>}</span>
      <time dateTime={post.publishedAt}>{ngayVN(post.publishedAt)}</time>
      {post.updatedAt && post.updatedAt > post.publishedAt && <span>Cập nhật <time dateTime={post.updatedAt}>{ngayVN(post.updatedAt)}</time></span>}
      <span>{post.readingMinutes} phút đọc</span>
    </div>
    <XemNhanh ds={videoCuaTrang(`/playbook/${post.slug}`)} path={`/playbook/${post.slug}`} />
    {post.coverImage && <PlaybookFigure priority image={{ url: post.coverImage, alt: post.coverAlt || `Ảnh minh họa: ${post.title}`, caption: post.coverCaption, fit: 'rong' }} />}
    {note && <p className="pb-note">{note}</p>}
    {toc.length > 1 && <nav className="article-toc" aria-label="Mục lục"><strong>Trong bài này</strong><ol>{toc.map(({ s, i }) => <li key={i}><a href={`#phan-${i + 1}`}>{s.heading}</a></li>)}</ol></nav>}
    {post.sections.map((section, index) => <Section section={section} index={index} key={index} />)}
    {post.storyType === 'spotlight' && <SpotlightKetNoi post={post} />}
    {post.templates?.map((slug) => <TemplateDownload key={slug} slug={slug} />)}
    {post.sources?.length > 0 && <section className="article-sources"><h2>Nguồn tham khảo</h2><ul>{post.sources.map((source) => <li key={source.url}><a href={source.url} target="_blank" rel="noreferrer">{source.label || source.url}</a></li>)}</ul></section>}
    <NextStep tieuDe="Áp dụng vào startup của bạn" nguon={`playbook:${post.slug}`} steps={buocCuaBai(post)} />
    {tram ? <TramTiepTheo tram={tram} /> : related.length > 0 && <aside className="pb-related" aria-labelledby="bai-lien-quan"><h2 id="bai-lien-quan">Đọc tiếp</h2><ul>{related.map((p) => <li key={p.slug}>
      <Link to={`/playbook/${p.slug}`}><span className="playbook-meta">{p.category}, {p.readingMinutes} phút</span><strong>{p.title}</strong></Link>
    </li>)}</ul></aside>}
    {post.author === ORG_AUTHOR && <p className="pb-share-inline">Bạn có trải nghiệm gọi vốn tương tự? <Link to="/playbook/gop-cau-chuyen">Kể cho chúng tôi</Link>, có thể ẩn danh.</p>}
    <p className="article-disclaimer">Nội dung nhằm mục đích tham khảo và chuẩn bị hồ sơ, không phải cam kết đầu tư hoặc tư vấn pháp lý.</p>
  </article>;
}
